<template>
  <div class="nixia-conversation-sidebar">
    <Button
      _key="btnNixiaNewConversation"
      type="primary"
      title="Nova conversa"
      classIcon="fas fa-plus"
      size="small"
      :clicked="newConversation"
    />

    <div class="nixia-conversation-sidebar-list">
      <div v-if="loadingConversations" class="nixia-conversation-sidebar-loading">
        <Loading
          type="bubbles"
          :center="false"
          :width="30"
          :height="30"
          color="var(--color-amber)"
        />
      </div>
      <template v-else>
        <div
          v-for="conversation in conversations"
          :key="conversation.id"
          class="nixia-conversation-sidebar-item"
          :class="{ active: conversation.id === currentConversationId }"
          @click="selectConversation(conversation.id)"
        >
          <span v-if="editingId === conversation.id" class="nixia-conversation-sidebar-item-edit-wrapper" @click.stop>
            <input
              :ref="`nixiaConversationSidebarEdit-${conversation.id}`"
              v-model="editingTitle"
              class="nixia-conversation-sidebar-item-edit"
              maxlength="120"
              :disabled="savingId === conversation.id"
              @keyup.enter="confirmEdit(conversation)"
              @keyup.esc="cancelEdit"
              @blur="confirmEdit(conversation)"
            />
            <Loading
              v-if="savingId === conversation.id"
              type="bubbles"
              :center="false"
              :width="22"
              :height="22"
              color="var(--color-brand)"
            />
          </span>
          <span
            v-else
            class="nixia-conversation-sidebar-item-title"
            @dblclick.stop="startEdit(conversation)"
          >{{ conversation.title }}</span>
          <Loading
            v-if="savingId !== conversation.id && isLoading(conversation.id)"
            type="bubbles"
            :center="false"
            :width="20"
            :height="20"
            color="var(--color-amber)"
          />
          <i
            v-else-if="savingId !== conversation.id"
            class="fa-solid fa-trash-can nixia-conversation-sidebar-item-remove"
            title="Excluir conversa"
            @click.stop="removeConversation(conversation.id)"
          ></i>
        </div>

        <div v-if="conversations.length === 0" class="nixia-conversation-sidebar-empty">
          Nenhuma conversa ainda
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Button from "@/ui/forms/Button";
import Loading from "@/ui/shared/Loading.vue";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";

export default {
  name: "NixiaConversationSidebar",
  components: { Button, Loading },
  data() {
    return {
      editingId: null,
      editingTitle: "",
      savingId: null,
      loadingConversations: false,
    };
  },
  computed: {
    ...mapState("nixia", ["conversations", "currentConversationId"]),
    ...mapGetters("generic", ["isLoading"]),
  },
  async mounted() {
    this.loadingConversations = true;
    await this.fetchConversations();
    this.loadingConversations = false;
  },
  methods: {
    ...mapMutations("generic", ["addLoading", "removeLoading"]),
    ...mapActions("nixia", [
      "fetchConversations",
      "createConversation",
      "fetchMessages",
      "deleteConversation",
      "renameConversation",
    ]),
    async newConversation() {
      try {
        await this.createConversation();
      } finally {
        this.removeLoading(["btnNixiaNewConversation"]);
      }
    },
    selectConversation(conversationId) {
      if (conversationId === this.currentConversationId) return;
      this.fetchMessages(conversationId);
    },
    async removeConversation(conversationId) {
      if (this.isLoading(conversationId)) return;
      this.addLoading(conversationId);
      try {
        await Promise.all([
          this.deleteConversation(conversationId),
          new Promise((resolve) => setTimeout(resolve, 400)),
        ]);
      } finally {
        this.removeLoading([conversationId]);
      }
    },
    startEdit(conversation) {
      this.editingId = conversation.id;
      this.editingTitle = conversation.title;
      this.$nextTick(() => {
        const input = this.$refs[`nixiaConversationSidebarEdit-${conversation.id}`];
        const field = Array.isArray(input) ? input[0] : input;
        if (field) {
          field.focus();
          field.select();
        }
      });
    },
    cancelEdit() {
      this.editingId = null;
      this.editingTitle = "";
    },
    async confirmEdit(conversation) {
      if (this.editingId !== conversation.id || this.savingId === conversation.id) return;
      const title = this.editingTitle.trim();
      if (!title || title === conversation.title) {
        this.editingId = null;
        return;
      }
      this.savingId = conversation.id;
      try {
        await this.renameConversation({ conversationId: conversation.id, title });
      } finally {
        this.savingId = null;
        this.editingId = null;
      }
    },
  },
};
</script>

<style scoped>
.nixia-conversation-sidebar {
  width: 260px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 12px;
  border-right: 1px solid var(--border-color);
  min-height: 0;
}

.nixia-conversation-sidebar-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nixia-conversation-sidebar-list::-webkit-scrollbar {
  width: 8px;
}

.nixia-conversation-sidebar-list::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: 20px;
}

.nixia-conversation-sidebar-list::-webkit-scrollbar-track {
  background-color: transparent;
}

.nixia-conversation-sidebar-loading {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

.nixia-conversation-sidebar-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--text-primary);
  font-size: 13px;
  transition: background 0.15s;
}

.nixia-conversation-sidebar-item:hover {
  background: var(--bg-hover);
}

.nixia-conversation-sidebar-item.active {
  background: var(--bg-elevated);
  font-weight: 500;
}

.nixia-conversation-sidebar-item-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nixia-conversation-sidebar-item-edit-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.nixia-conversation-sidebar-item-edit {
  flex: 1;
  min-width: 0;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: 4px;
  color: var(--input-color);
  font-size: 13px;
  padding: 2px 6px;
}

.nixia-conversation-sidebar-item-edit:focus {
  outline: none;
}

.nixia-conversation-sidebar-item-edit:disabled {
  opacity: 0.7;
}

.nixia-conversation-sidebar-item-remove {
  color: var(--text-muted);
  font-size: 12px;
  flex-shrink: 0;
}

.nixia-conversation-sidebar-item-remove:hover {
  color: var(--color-danger);
}

.nixia-conversation-sidebar-empty {
  padding: 12px;
  color: var(--text-muted);
  font-size: 13px;
  text-align: center;
}
</style>
